// App.js - POLISHED UX VERSION
import React, { useEffect, useState, useRef } from "react";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signOut
} from "firebase/auth";
import { getDatabase, ref, set } from "firebase/database";

const firebaseConfig = {
  apiKey: "AIzaSyAN5wX0lqe8jslUARlmKdd1u-MpmNb5QuU",
  authDomain: "filingtracker-20251028.firebaseapp.com",
  databaseURL: "https://filingtracker-20251028-default-rtdb.firebaseio.com",
  projectId: "filingtracker-20251028",
  storageBucket: "filingtracker-20251028.firebasestorage.app",
  messagingSenderId: "1085350029617",
  appId: "1:1085350029617:web:c472be3f4f68776bffa810",
  measurementId: "G-0WTBZV8NT6"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const database = getDatabase(app);

const FETCH_INTERVAL = 5000;
const USER_COUNT_INTERVAL = 15000;

// Toast notification component
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === "success" ? "#dcfce7" : type === "error" ? "#fee2e2" : "#dbeafe";
  const borderColor = type === "success" ? "#86efac" : type === "error" ? "#fecaca" : "#93c5fd";
  const textColor = type === "success" ? "#166534" : type === "error" ? "#991b1b" : "#1e40af";
  const icon = type === "success" ? "✓" : type === "error" ? "✗" : "ℹ";

  return (
    <div style={{
      position: "fixed",
      top: "20px",
      right: "20px",
      background: bgColor,
      border: `2px solid ${borderColor}`,
      color: textColor,
      padding: "12px 20px",
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      zIndex: 10000,
      animation: "slideIn 0.3s ease-out",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      minWidth: "250px",
      maxWidth: "400px",
    }}>
      <span style={{ fontSize: "1.2rem", fontWeight: "bold" }}>{icon}</span>
      <span style={{ flex: 1, fontWeight: 600 }}>{message}</span>
      <button
        onClick={onClose}
        style={{
          background: "none",
          border: "none",
          color: textColor,
          cursor: "pointer",
          fontSize: "1.2rem",
          padding: 0,
          lineHeight: 1,
        }}
      >
        ×
      </button>
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filings, setFilings] = useState([]);
  const [userCount, setUserCount] = useState(0);
  const [config, setConfig] = useState({ name: "", email: "" });
  const [emailSettings, setEmailSettings] = useState({ enabled: false, email: "" });
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [filterSettings, setFilterSettings] = useState({ minTransactionValue: 0 });
  const [error, setError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // Loading states for buttons
  const [savingConfig, setSavingConfig] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingFilter, setSavingFilter] = useState(false);
  const [clearing, setClearing] = useState(false);

  // Auth form
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [isSignup, setIsSignup] = useState(false);

  // Temp state for editing
  const [tempConfigName, setTempConfigName] = useState("");
  const [tempConfigEmail, setTempConfigEmail] = useState("");
  const [tempEmailEnabled, setTempEmailEnabled] = useState(false);
  const [tempEmailAddress, setTempEmailAddress] = useState("");
  const [tempMinTransactionValue, setTempMinTransactionValue] = useState(0);

  // Track previous filing count for sound
  const prevFilingCountRef = useRef(0);
  const audioRef = useRef(null);

  // Show toast notification
  function showToast(message, type = "info") {
    setToast({ message, type });
  }

  // Monitor auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('🔍 Auth State Changed:', firebaseUser);
      if (firebaseUser) {
        console.log('✅ User logged in:', firebaseUser.email);
        setUser(firebaseUser);
      } else {
        console.log('❌ No user');
        setUser(null);
        setFilings([]);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Fetch user count
  useEffect(() => {
    fetchUserCount();
    const interval = setInterval(fetchUserCount, USER_COUNT_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  // Load user config when logged in
  useEffect(() => {
    if (user) loadUserConfig();
  }, [user]);

  // Load email settings when logged in
  useEffect(() => {
    if (user) loadEmailSettings();
  }, [user]);

  // Load sound settings when logged in
  useEffect(() => {
    if (user) loadSoundSettings();
  }, [user]);

  // Load filter settings when logged in
  useEffect(() => {
    if (user) loadFilterSettings();
  }, [user]);

  // Poll filings when logged in
  useEffect(() => {
    if (!user) return;

    fetchFilings();
    const interval = setInterval(fetchFilings, FETCH_INTERVAL);
    return () => clearInterval(interval);
  }, [user]);

  // Play sound when new filings arrive
  useEffect(() => {
    if (filings.length > prevFilingCountRef.current && prevFilingCountRef.current > 0 && soundEnabled) {
      playSound();
    }
    prevFilingCountRef.current = filings.length;
  }, [filings, soundEnabled]);

  function playSound() {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(err => console.log('Sound play failed:', err));
    }
  }

  async function fetchUserCount() {
    try {
      const res = await fetch("https://sec-filings-tracker.onrender.com/api/user-count");
      const data = await res.json();
      setUserCount(data.count || 0);
    } catch (err) {
      console.error("Failed to fetch user count:", err);
    }
  }

  async function fetchFilings() {
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch("https://sec-filings-tracker.onrender.com/latest-filings", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (Array.isArray(data)) {
        setFilings((prev) => {
          const newIds = data.map(f => f.id);
          const filtered = prev.filter(p => !newIds.includes(p.id));
          return [...data, ...filtered].slice(0, 50);
        });
      } else if (data.error) {
        console.warn("Fetch filings error:", data.error);
      }
    } catch (err) {
      console.error("Error fetching filings:", err);
    }
  }

  async function loadUserConfig() {
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch("https://sec-filings-tracker.onrender.com/api/user-config", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setConfig(data);
      setTempConfigName(data.name || "");
      setTempConfigEmail(data.email || "");
    } catch (err) {
      console.error("Failed to load user config:", err);
    }
  }

  async function loadEmailSettings() {
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch("https://sec-filings-tracker.onrender.com/api/email-settings", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setEmailSettings(data);
      setTempEmailEnabled(data.enabled || false);
      setTempEmailAddress(data.email || "");
    } catch (err) {
      console.error("Failed to load email settings:", err);
    }
  }

  async function loadSoundSettings() {
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch("https://sec-filings-tracker.onrender.com/api/sound-settings", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setSoundEnabled(data.enabled || false);
    } catch (err) {
      console.error("Failed to load sound settings:", err);
      setSoundEnabled(false);
    }
  }

  async function loadFilterSettings() {
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch("https://sec-filings-tracker.onrender.com/api/filter-settings", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setFilterSettings(data);
      setTempMinTransactionValue(data.minTransactionValue || 0);
    } catch (err) {
      console.error("Failed to load filter settings:", err);
      setFilterSettings({ minTransactionValue: 0 });
    }
  }

  async function toggleSound() {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);

    try {
      const token = await auth.currentUser.getIdToken();
      await fetch("https://sec-filings-tracker.onrender.com/api/sound-settings", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ enabled: newValue }),
      });
      showToast(`Sound notifications ${newValue ? "enabled" : "disabled"}`, "success");
    } catch (err) {
      console.error("Failed to save sound settings:", err);
      showToast("Failed to save sound setting", "error");
      setSoundEnabled(!newValue); // Revert on error
    }
  }

  async function handleAuth() {
    if (!email.trim() || !password.trim()) {
      setError("Email and password are required");
      return;
    }

    setAuthLoading(true);
    setError("");

    try {
      if (isSignup) {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const uid = userCredential.user.uid;

        // Initialize user data in database
        await set(ref(database, `users/${uid}/config`), {
          name: name || email.split('@')[0],
          email: email,
          userAgent: `${name || email.split('@')[0]} (mailto:${email})`,
        });
        await set(ref(database, `users/${uid}/emailSettings`), {
          enabled: false,
          email: email,
        });
        await set(ref(database, `users/${uid}/soundSettings`), {
          enabled: false,
        });
        await set(ref(database, `users/${uid}/filterSettings`), {
          minTransactionValue: 0,
        });

        showToast("Account created successfully!", "success");
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        showToast("Logged in successfully!", "success");
      }

      setEmail("");
      setPassword("");
      setName("");
    } catch (err) {
      console.error("Auth error:", err);
      const friendlyError = err.message.includes("email-already-in-use") 
        ? "This email is already registered"
        : err.message.includes("wrong-password") || err.message.includes("user-not-found")
        ? "Invalid email or password"
        : err.message.includes("weak-password")
        ? "Password must be at least 6 characters"
        : "Authentication failed. Please try again.";
      setError(friendlyError);
    } finally {
      setAuthLoading(false);
    }
  }

  async function handleLogout() {
    try {
      await signOut(auth);
      setFilings([]);
      setConfig({ name: "", email: "" });
      setEmailSettings({ enabled: false, email: "" });
      setSoundEnabled(false);
      setFilterSettings({ minTransactionValue: 0 });
      showToast("Logged out successfully", "success");
    } catch (err) {
      setError(err.message);
    }
  }

  async function saveUserConfig() {
    if (!tempConfigName.trim() || !tempConfigEmail.trim()) {
      showToast("Name and email are required", "error");
      return;
    }

    setSavingConfig(true);
    setError("");

    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch("https://sec-filings-tracker.onrender.com/api/user-config", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: tempConfigName, email: tempConfigEmail }),
      });

      const data = await res.json();

      if (data.error) {
        showToast(data.error, "error");
      } else {
        setConfig({ name: tempConfigName, email: tempConfigEmail });
        showToast("User settings saved!", "success");
      }
    } catch (err) {
      console.error("Error saving config:", err);
      showToast("Failed to save settings", "error");
    } finally {
      setSavingConfig(false);
    }
  }

  async function saveEmailSettings() {
    if (!tempEmailAddress.trim()) {
      showToast("Email address is required", "error");
      return;
    }

    setSavingEmail(true);
    setError("");

    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch("https://sec-filings-tracker.onrender.com/api/email-settings", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ enabled: tempEmailEnabled, email: tempEmailAddress }),
      });

      const data = await res.json();

      if (data.error) {
        showToast(data.error, "error");
      } else {
        setEmailSettings({ enabled: tempEmailEnabled, email: tempEmailAddress });
        showToast("Email settings saved!", "success");
      }
    } catch (err) {
      console.error("Error saving email settings:", err);
      showToast("Failed to save email settings", "error");
    } finally {
      setSavingEmail(false);
    }
  }

  async function saveFilterSettings() {
    setSavingFilter(true);
    setError("");

    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch("https://sec-filings-tracker.onrender.com/api/filter-settings", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ minTransactionValue: tempMinTransactionValue }),
      });

      const data = await res.json();

      if (data.error) {
        showToast(data.error, "error");
      } else {
        setFilterSettings({ minTransactionValue: tempMinTransactionValue });
        showToast("Filter settings saved!", "success");
      }
    } catch (err) {
      console.error("Error saving filter settings:", err);
      showToast("Failed to save filter", "error");
    } finally {
      setSavingFilter(false);
    }
  }

  async function clearFilings() {
    if (!window.confirm("Clear all seen filings? This cannot be undone.")) return;

    setClearing(true);

    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch("https://sec-filings-tracker.onrender.com/api/clear-filings", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();

      if (data.error) {
        showToast(data.error, "error");
      } else {
        setFilings([]);
        showToast("All filings cleared!", "success");
      }
    } catch (err) {
      console.error("Error clearing filings:", err);
      showToast("Failed to clear filings", "error");
    } finally {
      setClearing(false);
    }
  }

  if (loading) {
    return (
      <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        fontFamily: "system-ui, Arial, sans-serif",
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{
            width: "60px",
            height: "60px",
            border: "6px solid #e5e7eb",
            borderTop: "6px solid #3b82f6",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto 20px",
          }} />
          <h2 style={{ margin: "0 0 8px 0" }}>Loading...</h2>
          <p style={{ color: "#6b7280", margin: 0 }}>Connecting to Firebase</p>
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{
        fontFamily: "system-ui, Arial, sans-serif",
        maxWidth: 400,
        margin: "60px auto",
        padding: 30,
        background: "#fff",
        borderRadius: 12,
        boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
      }}>
        <style>{`
          button:not(:disabled):active {
            transform: scale(0.97);
            box-shadow: inset 0 2px 4px rgba(0,0,0,0.2);
          }
          button {
            transition: transform 0.1s ease, box-shadow 0.1s ease;
          }
        `}</style>
        
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        
        <h1 style={{ marginBottom: 8, fontSize: "1.8rem" }}>SEC Filings Tracker</h1>
        <p style={{ color: "#6b7280", marginBottom: 24 }}>
          👥 Active Users: <strong>{userCount}</strong>
        </p>

        {error && (
          <div style={{
            background: "#fee2e2",
            color: "#991b1b",
            padding: "12px 16px",
            borderRadius: "8px",
            marginBottom: "16px",
            border: "1px solid #fecaca",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
            <span>{error}</span>
            <button
              onClick={() => setError("")}
              style={{
                background: "none",
                border: "none",
                color: "#991b1b",
                cursor: "pointer",
                fontSize: "1.2rem",
                padding: 0,
              }}
            >
              ×
            </button>
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAuth()}
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: 6,
              border: "1px solid #d1d5db",
              marginBottom: "8px",
              fontSize: "1rem",
              boxSizing: "border-box",
            }}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAuth()}
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: 6,
              border: "1px solid #d1d5db",
              marginBottom: "8px",
              fontSize: "1rem",
              boxSizing: "border-box",
            }}
          />
          {isSignup && (
            <input
              type="text"
              placeholder="Name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: 6,
                border: "1px solid #d1d5db",
                marginBottom: "8px",
                fontSize: "1rem",
                boxSizing: "border-box",
              }}
            />
          )}
        </div>

        <button
          onClick={handleAuth}
          disabled={authLoading}
          className="btn-primary"
          style={{
            width: "100%",
            padding: "12px",
            borderRadius: 6,
            border: "none",
            background: authLoading ? "#9ca3af" : "#16a34a",
            color: "white",
            fontWeight: 600,
            cursor: authLoading ? "not-allowed" : "pointer",
            marginBottom: "8px",
            fontSize: "1rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            transition: "transform 0.1s, box-shadow 0.1s",
          }}
        >
          {authLoading && (
            <div style={{
              width: "16px",
              height: "16px",
              border: "2px solid white",
              borderTop: "2px solid transparent",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
            }} />
          )}
          {authLoading ? "Please wait..." : (isSignup ? "Sign Up" : "Login")}
        </button>
        <button
          onClick={() => setIsSignup(!isSignup)}
          disabled={authLoading}
          style={{
            width: "100%",
            padding: "12px",
            borderRadius: 6,
            border: "1px solid #d1d5db",
            background: "white",
            fontWeight: 600,
            cursor: authLoading ? "not-allowed" : "pointer",
            fontSize: "1rem",
          }}
        >
          {isSignup ? "Already have an account? Login" : "Need an account? Sign Up"}
        </button>
      </div>
    );
  }

  // Logged in view
  return (
    <div style={{
      padding: "40px",
      fontFamily: "system-ui, Arial, sans-serif",
      maxWidth: "1000px",
      margin: "0 auto",
    }}>
      <style>{`
        button:not(:disabled):active {
          transform: scale(0.97);
          box-shadow: inset 0 2px 4px rgba(0,0,0,0.2);
        }
        button {
          transition: transform 0.1s ease, box-shadow 0.1s ease;
        }
      `}</style>
      
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      
      {/* Audio element for notification sound */}
      <audio ref={audioRef} src="https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3" />

      <header style={{ marginBottom: "32px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: "2rem" }}>SEC Filings Tracker</h1>
            <p style={{ color: "#6b7280", margin: "8px 0 0 0" }}>
              {config.name || user.email} | 👥 {userCount} users
            </p>
          </div>
          <div style={{ display: "flex", gap: "12px" }}>
            <button
              onClick={toggleSound}
              style={{
                padding: "8px 16px",
                borderRadius: 6,
                border: "1px solid #d1d5db",
                background: soundEnabled ? "#16a34a" : "white",
                color: soundEnabled ? "white" : "#000",
                cursor: "pointer",
                fontWeight: 600,
                transition: "all 0.2s",
              }}
            >
              🔔 Sound {soundEnabled ? "ON" : "OFF"}
            </button>
            <button
              onClick={handleLogout}
              style={{
                padding: "8px 16px",
                borderRadius: 6,
                border: "1px solid #d1d5db",
                background: "white",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Logout
            </button>
          </div>
        </div>
        <div style={{
          background: "#d1fae5",
          padding: "12px 16px",
          borderRadius: "8px",
          border: "1px solid #6ee7b7",
        }}>
          <p style={{ margin: 0, fontSize: "0.9rem", color: "#065f46" }}>
            ✓ Connected to Firebase - Multi-user mode active
            {filterSettings.minTransactionValue > 0 && (
              <span style={{ marginLeft: "12px" }}>
                | 🔍 Filter: Min ${filterSettings.minTransactionValue.toLocaleString()}
              </span>
            )}
          </p>
        </div>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "24px", marginBottom: "32px" }}>
        {/* User Config */}
        <section style={{
          background: "#fff",
          padding: "24px",
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
        }}>
          <h2 style={{ marginTop: 0, fontSize: "1.2rem" }}>User Settings</h2>
          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", marginBottom: "4px", fontWeight: 600, fontSize: "0.9rem" }}>
              Name:
            </label>
            <input
              type="text"
              value={tempConfigName}
              onChange={(e) => setTempConfigName(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                fontSize: "1rem",
                boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", marginBottom: "4px", fontWeight: 600, fontSize: "0.9rem" }}>
              Email:
            </label>
            <input
              type="email"
              value={tempConfigEmail}
              onChange={(e) => setTempConfigEmail(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                fontSize: "1rem",
                boxSizing: "border-box",
              }}
            />
          </div>
          <button
            onClick={saveUserConfig}
            disabled={savingConfig}
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: "6px",
              border: "none",
              background: savingConfig ? "#9ca3af" : "#6366f1",
              color: "white",
              fontWeight: 600,
              cursor: savingConfig ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            {savingConfig && (
              <div style={{
                width: "16px",
                height: "16px",
                border: "2px solid white",
                borderTop: "2px solid transparent",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
              }} />
            )}
            {savingConfig ? "Saving..." : "Update Config"}
          </button>
        </section>

        {/* Email Notifications */}
        <section style={{
          background: "#fff",
          padding: "24px",
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
        }}>
          <h2 style={{ marginTop: 0, fontSize: "1.2rem" }}>Email Notifications</h2>
          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={tempEmailEnabled}
                onChange={(e) => setTempEmailEnabled(e.target.checked)}
                style={{ width: "18px", height: "18px" }}
              />
              <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>Enable Email Alerts</span>
            </label>
          </div>
          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", marginBottom: "4px", fontWeight: 600, fontSize: "0.9rem" }}>
              Notification Email:
            </label>
            <input
              type="email"
              value={tempEmailAddress}
              onChange={(e) => setTempEmailAddress(e.target.value)}
              placeholder="your-email@example.com"
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                fontSize: "1rem",
                boxSizing: "border-box",
              }}
            />
          </div>
          <button
            onClick={saveEmailSettings}
            disabled={savingEmail}
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: "6px",
              border: "none",
              background: savingEmail ? "#9ca3af" : "#16a34a",
              color: "white",
              fontWeight: 600,
              cursor: savingEmail ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            {savingEmail && (
              <div style={{
                width: "16px",
                height: "16px",
                border: "2px solid white",
                borderTop: "2px solid transparent",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
              }} />
            )}
            {savingEmail ? "Saving..." : "Update Email Settings"}
          </button>
        </section>

        {/* Filter Settings */}
        <section style={{
          background: "#fff",
          padding: "24px",
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
        }}>
          <h2 style={{ marginTop: 0, fontSize: "1.2rem" }}>Transaction Filter</h2>
          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", marginBottom: "4px", fontWeight: 600, fontSize: "0.9rem" }}>
              Minimum Transaction Value:
            </label>
            <select
              value={tempMinTransactionValue}
              onChange={(e) => setTempMinTransactionValue(Number(e.target.value))}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                fontSize: "1rem",
                boxSizing: "border-box",
              }}
            >
              <option value={0}>Show All</option>
              <option value={50000}>$50,000+</option>
              <option value={100000}>$100,000+</option>
              <option value={250000}>$250,000+</option>
              <option value={500000}>$500,000+</option>
              <option value={1000000}>$1,000,000+</option>
              <option value={5000000}>$5,000,000+</option>
            </select>
          </div>
          <div style={{ marginBottom: "16px", padding: "12px", background: "#f9fafb", borderRadius: "6px" }}>
            <p style={{ margin: 0, fontSize: "0.85rem", color: "#6b7280" }}>
              Only show buy/sell signals above this transaction value. Helps filter out small, routine trades.
            </p>
          </div>
          <button
            onClick={saveFilterSettings}
            disabled={savingFilter}
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: "6px",
              border: "none",
              background: savingFilter ? "#9ca3af" : "#f59e0b",
              color: "white",
              fontWeight: 600,
              cursor: savingFilter ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            {savingFilter && (
              <div style={{
                width: "16px",
                height: "16px",
                border: "2px solid white",
                borderTop: "2px solid transparent",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
              }} />
            )}
            {savingFilter ? "Saving..." : "Update Filter"}
          </button>
        </section>
      </div>

      {/* Controls */}
      <div style={{ marginBottom: "24px", display: "flex", gap: "12px" }}>
        <button
          onClick={clearFilings}
          disabled={clearing}
          style={{
            padding: "10px 20px",
            borderRadius: "6px",
            border: "none",
            background: clearing ? "#9ca3af" : "#ef4444",
            color: "white",
            fontWeight: 600,
            cursor: clearing ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {clearing && (
            <div style={{
              width: "16px",
              height: "16px",
              border: "2px solid white",
              borderTop: "2px solid transparent",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
            }} />
          )}
          {clearing ? "Clearing..." : "Clear All Filings"}
        </button>
        <button
          onClick={fetchFilings}
          style={{
            padding: "10px 20px",
            borderRadius: "6px",
            border: "1px solid #d1d5db",
            background: "white",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Refresh Now
        </button>
      </div>

      {/* Filings Table */}
      <section>
        <h2 style={{ fontSize: "1.4rem", marginBottom: "16px" }}>
          Recent Filings ({filings.length})
        </h2>
        {filings.length === 0 ? (
          <div style={{
            padding: "40px",
            textAlign: "center",
            background: "#f9fafb",
            borderRadius: "12px",
            color: "#6b7280",
          }}>
            No filings yet. Monitoring SEC RSS feed...
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{
              width: "100%",
              borderCollapse: "collapse",
              background: "#fff",
              borderRadius: "12px",
              overflow: "hidden",
              border: "1px solid #e5e7eb",
            }}>
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  <th style={{ padding: "12px", textAlign: "left", borderBottom: "2px solid #e5e7eb" }}>Company</th>
                  <th style={{ padding: "12px", textAlign: "left", borderBottom: "2px solid #e5e7eb" }}>Type</th>
                  <th style={{ padding: "12px", textAlign: "left", borderBottom: "2px solid #e5e7eb" }}>Verdict</th>
                  <th style={{ padding: "12px", textAlign: "left", borderBottom: "2px solid #e5e7eb" }}>Value</th>
                  <th style={{ padding: "12px", textAlign: "left", borderBottom: "2px solid #e5e7eb" }}>Date</th>
                  <th style={{ padding: "12px", textAlign: "left", borderBottom: "2px solid #e5e7eb" }}>Link</th>
                </tr>
              </thead>
              <tbody>
                {filings.map((f, idx) => (
                  <tr key={f.id} style={{ borderBottom: idx < filings.length - 1 ? "1px solid #e5e7eb" : "none" }}>
                    <td style={{ padding: "12px", maxWidth: "300px" }}>{f.title}</td>
                    <td style={{ padding: "12px" }}>{f.type}</td>
                    <td style={{
                      padding: "12px",
                      fontWeight: 600,
                      color: f.verdict === "buy" ? "#16a34a" : f.verdict === "sell" ? "#dc2626" : "#6b7280",
                    }}>
                      {f.verdict.toUpperCase()}
                    </td>
                    <td style={{ padding: "12px", fontWeight: 600 }}>
                      {f.transactionValue ? `${f.transactionValue.toLocaleString()}` : "N/A"}
                    </td>
                    <td style={{ padding: "12px", fontSize: "0.9rem", color: "#6b7280" }}>
                      {new Date(f.updated).toLocaleString()}
                    </td>
                    <td style={{ padding: "12px" }}>
                      {f.link ? (
                        <a
                          href={f.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#3b82f6", textDecoration: "none" }}
                        >
                          View →
                        </a>
                      ) : (
                        <span style={{ color: "#9ca3af" }}>N/A</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}